import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  alpha,
  useTheme,
  Breadcrumbs,
  Link
} from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GetAppIcon from '@mui/icons-material/GetApp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HomeIcon from '@mui/icons-material/Home';
import HistoryIcon from '@mui/icons-material/History';
import TransactionsTable from '../components/TransactionsTable';
import apiService from '../services/api';
import { auth } from '../firebase';
//import CheckCircleIcon from "@mui/icons-material/CheckCircle";
//import CategoryIcon from "@mui/icons-material/Category";
import Tooltip from "@mui/material/Tooltip";




function downloadBlob(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// Helper function for auto-categorization
const getAutoCategory = (description: string): string => {
  if (!description) return '❓ Uncategorized';
  
  const text = description.toLowerCase();
  
  // Income
  if (text.includes('funds transfer from') || text.includes('deposit') || text.includes('salary') || text.includes('interest payment')) {
    return '💰 Income';
  }
  // Transfers
  if (text.includes('transfer to') || text.includes('withdrawal to') || text.includes('contribution')) {
    return '🔄 Transfer';
  }
  // Gifts
  if (text.includes('gift')) return '🎁 Gift';
  // Loans
  if (text.includes('loan')) return '💰 Loan';
  // Travel
  if (text.includes('flight') || text.includes('book')) return '✈️ Travel';
  // Housing
  if (text.includes('home stuff') || text.includes('rent') || text.includes('mortgage')) return '🏠 Housing';
  // Business
  if (text.includes('linkedin') || text.includes('google') || text.includes('microsoft') || text.includes('airtasker')) {
    return '💼 Business';
  }
  // Food
  if (text.includes('pizza') || text.includes('pasta') || text.includes('cafe') || text.includes('restaurant')) {
    return '🍔 Food & Dining';
  }
  // Shopping
  if (text.includes('coles') || text.includes('woolworths') || text.includes('kmart') || text.includes('target')) {
    return '🛒 Shopping';
  }
  
  return '❓ Uncategorized';
};

const cleanCategory = (category: string): string => {
  if (!category) return 'Uncategorized';

  return category
    .replace(/[^\w\s&/-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};


const Transactions: React.FC = () => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobName, setJobName] = useState<string>('');
  const [includeCategories, setIncludeCategories] = useState(false);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate("/login");
      return;
    }
    loadTransactions();
  }, [jobId, navigate]);

 const loadTransactions = async () => {
  setLoading(true);
  setError(null);

  try {
    const data = await apiService.getTransactions(jobId!);
    const extractedTransactions = data?.transactions ?? data?.value ?? (Array.isArray(data) ? data : []);
    
    // Add category field to each transaction - PRESERVE existing categories!
    const withCategories = extractedTransactions.map((t: any) => {
      // Check if transaction already has a category from database
      let existingCategory = t.ppp_category || t.category || t.Category;
      
      // If no existing category, try to auto-categorize
      if (!existingCategory || existingCategory === "❓ Uncategorized") {
        existingCategory = getAutoCategory(t.ppp_description || t.description || '');
      }
      
      return {
        ...t,
        ppp_category: existingCategory || "❓ Uncategorized"
      };
    });
    
    setTransactions(withCategories);
    
       
    // Try to get filename from multiple sources
    let fileName = '';
    
    // Check sessionStorage first
    fileName = sessionStorage.getItem(`job_${jobId}_name`) || '';
    
    // If not in sessionStorage, try to get from data
    if (!fileName && data?.fileName) {
      fileName = data.fileName;
    }
    
    // If still empty, try to fetch from jobs API
    if (!fileName) {
      try {
        const jobs = await apiService.getJobs();
        const currentJob = jobs.find((j: any) => j.ppp_sf_jobid === jobId);
        if (currentJob && currentJob.ppp_sourcefilename) {
          fileName = currentJob.ppp_sourcefilename;
          sessionStorage.setItem(`job_${jobId}_name`, fileName);
        }
      } catch (e) {
        console.warn('Could not fetch job name:', e);
      }
    }
    
    if (fileName) {
      setJobName(fileName);
    } else {
      setJobName(`Job ${jobId?.slice(0, 8)}`);
    }
  } catch (err: any) {
    console.error("Load transactions error:", err);
    setError(err.message || "Failed to load transactions");
    if (err.response?.status === 401) {
      setTimeout(() => navigate("/login"), 2000);
    }
  } finally {
    setLoading(false);
  }
};

 const handleExportCSV = async () => {
  try {
    // Get original filename from jobName or sessionStorage
    let originalFileName = jobName;

    // If jobName is empty or generic, try to get from sessionStorage
    if (!originalFileName || originalFileName === `Job ${jobId?.slice(0, 8)}`) {
      originalFileName = sessionStorage.getItem(`job_${jobId}_name`) || '';
    }

    // If still empty, fetch from API
    if (!originalFileName) {
      try {
        const jobs = await apiService.getJobs();
        const currentJob = jobs.find((j: any) => j.ppp_sf_jobid === jobId);
        if (currentJob && currentJob.ppp_sourcefilename) {
          originalFileName = currentJob.ppp_sourcefilename;
          sessionStorage.setItem(`job_${jobId}_name`, originalFileName);
        }
      } catch (e) {
        console.warn('Could not fetch job name:', e);
      }
    }

    // Remove .pdf extension if present
    const baseFileName = originalFileName
      ? originalFileName.replace(/\.pdf$/i, '')
      : `statement_${jobId?.slice(0, 8)}`;

    const csvRows = [
      includeCategories
        ? ['Date', 'Description', 'Amount', 'Balance', 'Category']
        : ['Date', 'Description', 'Amount', 'Balance'],

      ...transactions.map(t => {
        let dateStr = '';

        if (t.ppp_transactiondate) {
          const date = new Date(t.ppp_transactiondate);
          dateStr = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}/${date.getFullYear()}`;
        } else if (t.date) {
          dateStr = t.date;
        } else if (t.dateIso) {
          const date = new Date(t.dateIso);
          dateStr = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}/${date.getFullYear()}`;
        }

        const baseRow = [
          dateStr,
          t.ppp_description || t.description || '',
          t.ppp_amountsigned || t.amountSigned || t.amount || 0,
          t.ppp_balance ?? t.balance ?? ''
        ];

        if (includeCategories) {
          return [
            ...baseRow,
            cleanCategory(t.ppp_category || 'Uncategorized')
          ];
        }

        return baseRow;
      })
    ];

    const csvContent = csvRows
      .map(row =>
        row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')
      )
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    downloadBlob(
      blob,
      includeCategories
        ? `${baseFileName}_with_categories.csv`
        : `${baseFileName}.csv`
    );
  } catch (error) {
    console.error('Export failed:', error);
    setError('Failed to export CSV');
  }
};

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>
          Loading transactions...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            background: "linear-gradient(135deg, #0A1E3C 0%, #1E3A5F 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1
          }}
        >
          Transaction Details
        </Typography>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon sx={{ fontSize: 18 }} />
            Home
          </Link>
          <Link
            color="inherit"
            onClick={() => navigate('/history')}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HistoryIcon sx={{ fontSize: 18 }} />
            History
          </Link>
          <Typography color="text.primary">Transactions</Typography>
        </Breadcrumbs>
      </Box>

      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ReceiptIcon sx={{ color: theme.palette.secondary.main }} />
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Statement
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {jobName || `Job ${jobId?.slice(0, 8)}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {transactions.length} transactions extracted
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
         <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
  {/* Segmented Control for Categories */}
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      bgcolor: mode === 'light' ? alpha(theme.palette.primary.main, 0.04) : alpha(theme.palette.primary.main, 0.08),
      borderRadius: 3,
      p: 0.5,
    }}
  >
    <Tooltip title="Export without categories (raw transaction data)">
      <Button
        disableRipple={!includeCategories}
        onClick={() => setIncludeCategories(false)}
        sx={{
          borderRadius: 2,
          px: 2,
          py: 0.75,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8rem',
          transition: 'all 0.2s ease',
          ...(!includeCategories 
            ? {
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }
            : {
                color: mode === 'light' ? '#0A1E3C' : '#E5E5E5',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                }
              }
          ),
        }}
      >
        📄 Raw Data
      </Button>
    </Tooltip>
    
    <Tooltip title="Export with AI-generated categories">
      <Button
        disableRipple={includeCategories}
        onClick={() => setIncludeCategories(true)}
        sx={{
          borderRadius: 2,
          px: 2,
          py: 0.75,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8rem',
          transition: 'all 0.2s ease',
          ...(includeCategories 
            ? {
                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                color: '#0A1E3C',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }
            : {
                color: mode === 'light' ? '#0A1E3C' : '#E5E5E5',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                }
              }
          ),
        }}
      >
        🏷️ With Categories
      </Button>
    </Tooltip>
  </Box>

  <Button
    startIcon={<ArrowBackIcon />}
    onClick={() => navigate('/history')}
    variant="outlined"
    sx={{
      borderColor: mode === 'light' ? '#0A1E3C' : alpha(theme.palette.secondary.main, 0.5),
      color: mode === 'light' ? '#0A1E3C' : '#E5E5E5',
      '&:hover': {
        borderColor: theme.palette.secondary.main,
        bgcolor: alpha(theme.palette.secondary.main, 0.04)
      }
    }}
  >
    Back to History
  </Button>

  <Button
    variant="contained"
    startIcon={<GetAppIcon />}
    onClick={handleExportCSV}
    disabled={transactions.length === 0}
    sx={{
      background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
      color: '#0A1E3C',
      '&:hover': {
        background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`
      }
    }}
  >
    Export CSV
  </Button>
</Box>
        </Box>
      </Paper>

      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${mode === 'light' ? 'rgba(10, 30, 60, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
          boxShadow: mode === 'light' ? '0 4px 20px rgba(0, 0, 0, 0.02)' : '0 4px 20px rgba(0, 0, 0, 0.4)'
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {transactions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ReceiptIcon sx={{ fontSize: 60, color: theme.palette.secondary.main, opacity: 0.5, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No transactions found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This statement may still be processing or has no transactions
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={<ReceiptIcon />}
                label={`${transactions.length} Transactions`}
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                  fontWeight: 600
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Click on any row to edit transaction details
              </Typography>
            </Box>

            {jobId ? (
              <TransactionsTable
                jobId={jobId}
                transactions={transactions}
                onSelectionChange={() => {}}
                onDataChanged={loadTransactions}
              />
            ) : (
              <Typography>Loading transactions...</Typography>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default Transactions;